import { ReactNode } from "react";

import { Action } from "types/generated";

export const RAW_CONFIG = [
  {
    id: "stacks",
    title: "Stacks",
    permissions: [
      Action.StackManage,
      Action.StackUpdate,
      Action.StackDelete,
      Action.StackLock,
      Action.StackUnlock,
      Action.StackUnlockForce,
      Action.StackAddConfig,
      Action.StackDeleteConfig,
      Action.StackReslug,
      Action.StackSetCurrentCommit,
      Action.StackStateDownload,
      Action.StackSyncCommit,
      Action.StackUploadLocalWorkspace,
      Action.StackManagedStateRollback,
      Action.StackManagedStateImport,
      Action.StackDisable,
      Action.StackEnable,
    ],
  },
  {
    title: "Runs",
    id: "run",
    permissions: [
      Action.RunTrigger,
      Action.RunTriggerWithCustomRuntimeConfig,
      Action.RunConfirm,
      Action.RunDiscard,
      Action.RunReview,
      Action.RunCancel,
      Action.RunCancelBlocking,
      Action.RunRetry,
      Action.RunRetryBlocking,
      Action.RunStop,
      Action.RunStopBlocking,
      Action.RunKill,
      Action.RunKillBlocking,
      Action.RunPromote,
      Action.RunProposeLocalWorkspace,
      Action.RunProposeWithOverrides,
      Action.RunComment,
      Action.RunTargetedReplan,
      Action.RunPrioritizeSet,
    ],
  },
  {
    id: "scheduling",
    title: "Scheduling",
    permissions: [
      Action.DriftDetectionIntegrationCreate,
      Action.DriftDetectionIntegrationUpdate,
      Action.DriftDetectionIntegrationDelete,
      Action.StackScheduledDeleteCreate,
      Action.StackScheduledDeleteUpdate,
      Action.StackScheduledDeleteDelete,
      Action.StackScheduledTaskCreate,
      Action.StackScheduledTaskUpdate,
      Action.StackScheduledTaskDelete,
      Action.StackScheduledRunCreate,
      Action.StackScheduledRunUpdate,
      Action.StackScheduledRunDelete,
    ],
  },
  {
    id: "space",
    title: "Space",
    permissions: [Action.SpaceRead, Action.SpaceShareModule],
  },
  {
    id: "tasks",
    title: "Tasks",
    permissions: [Action.TaskCreate],
  },
  {
    id: "context",
    title: "Context",
    permissions: [Action.ContextCreate, Action.ContextUpdate, Action.ContextDelete],
  },
  {
    id: "worker-pool",
    title: "Worker Pool",
    permissions: [
      Action.WorkerPoolCreate,
      Action.WorkerPoolUpdate,
      Action.WorkerPoolDelete,
      Action.WorkerPoolReset,
      Action.WorkerPoolCycle,
      Action.WorkerDrainSet,
    ],
  },
  {
    id: "module",
    title: "Module",
    permissions: [
      Action.ModuleCreate,
      Action.ModuleDisable,
      Action.ModuleEnable,
      Action.ModuleTriggerVersion,
      Action.ModuleUploadLocalWorkspace,
      Action.ModuleProposeLocalWorkspace,
      Action.ModuleVersionDelete,
      Action.ModulePublish,
      Action.ModuleMarkAsBad,
    ],
  },
  {
    id: "terraform",
    title: "Terraform Provider",
    permissions: [
      Action.TerraformProviderCreate,
      Action.TerraformProviderUpdate,
      Action.TerraformProviderDelete,
      Action.TerraformProviderVersionCreate,
      Action.TerraformProviderVersionUpdate,
      Action.TerraformProviderVersionDelete,
      Action.TerraformProviderVersionRevoke,
      Action.TerraformProviderVersionPublish,
      Action.TerraformProviderSetVisibility,
      Action.TerraformProviderVersionRegisterPlatform,
    ],
  },

  {
    id: "intent",
    title: "Intent",
    permissions: [
      Action.IntentProjectEnable,
      Action.IntentProjectDisable,
      Action.IntentProjectCreate,
      Action.IntentProjectUpdate,
      Action.IntentProjectDelete,
      Action.IntentProjectLock,
      Action.IntentProjectUnlock,
      Action.IntentProjectConfigAdd,
      Action.IntentProjectConfigUpdate,
      Action.IntentProjectConfigDelete,
      Action.IntentProjectCloudIntegrationAttach,
      Action.IntentProjectCloudIntegrationDetach,
      Action.IntentProjectPolicyAttach,
      Action.IntentProjectPolicyDetach,
      Action.IntentResourceCreate,
      Action.IntentResourceUpdate,
      Action.IntentResourceDelete,
      Action.IntentResourceImport,
      Action.IntentResourceRefresh,
      Action.IntentResourceResume,
      Action.IntentResourceOperationReview,
      Action.IntentStateRead,
      Action.IntentStateEject,
      Action.IntentPolicyCreate,
      Action.IntentPolicyUpdate,
      Action.IntentPolicyDelete,
      Action.IntentDependenciesAdd,
      Action.IntentDependenciesRemove,
      Action.AiIntegrationCreate,
      Action.AiIntegrationUpdate,
      Action.AiIntegrationToggle,
      Action.AiIntegrationDelete,
    ],
  },
  {
    id: "templates",
    title: "Templates",
    permissions: [
      Action.CreateBlueprintsVersionedGroup,
      Action.UpdateBlueprintsVersionedGroup,
      Action.DeleteBlueprintsVersionedGroup,
      Action.CreateBlueprintDeployment,
      Action.UpgradeBlueprintDeployment,
      Action.DeleteBlueprintDeployment,
    ],
  },
];

// Define which permissions are recommended (and pre-selected by default for new roles)
export const RECOMMENDED_PERMISSIONS = new Set([Action.SpaceRead]);

// Helper functions to check if permission/section is recommended
export const isRecommendedPermission = (action: Action): boolean =>
  RECOMMENDED_PERMISSIONS.has(action);

/**
 * Check if a section has any recommended permissions.
 * This should be computed based on the section's permissions passed from the config.
 * @param permissions Array of permissions in the section
 * @returns true if the section has any recommended permissions
 */
export const isSectionRecommended = (
  permissions: Array<{ action: Action; id: string; description: string }>
): boolean => permissions.some((permission) => RECOMMENDED_PERMISSIONS.has(permission.action));

// Helper function to get warning message for a specific permission when unchecked
export const getPermissionWarningMessage = (
  action: Action,
  isChecked: boolean
): ReactNode | undefined => {
  if (isChecked) return undefined;

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (action) {
    case Action.SpaceRead:
      return (
        <>
          Needed to access space-scoped resources unless custom role is combined with the system{" "}
          <strong>"Space Read"</strong> role.
        </>
      );
    default:
      return undefined;
  }
};
